import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    SegmentLoader,
    ButtonGroup,
    Button,
    Row,
    Col,
    FieldsetControl,
    RadioControl,
} from '@si-armes/dsfr-react';
import { useForm, FormProvider } from 'react-hook-form';
import PropTypes from 'prop-types';
import { postAPI, reinitialiseAPI } from 'store/apiReducer';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_COMPTE_CREER_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR_URL,
    API_POST_VERIFIER_BAN,
    CONTEXT_CREER_COMPTE_ETAPE_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR,
    CONTEXT_POST_VERIFIER_BAN,
} from 'constants/apiUrls.constants';
import TableAdressesComponent from 'components/table/TableAdressesComponent';
import FormulaireAdresseSimple from 'components/form/formulaireAdresse/FormulaireAdresseSimple';
import ModalBan from 'components/modalBan/ModalBan';
import FormaterCreationListeAdressesSecondaires from '../adresseSecondaire/FormaterCreationListeAdressesSecondaires';
import InfoChampsObligatoire from '../../../../../../components/form/infoObligatoire/InfoChampsObligatoire';

const CreerCompteAdresseSecondaireResponsableLegal = ({
    uuidBrouillon,
    setEtapeAdresse,
    retourEtape,
    postApi,
    reinitialiseApi,
    dataBan,
    isLoadingBan,
    isLoadingAjoutAdresseSecondaireResponsableLegalMineur,
    dataAjoutAdresseSecondaireResponsableLegalMineur,
    dataAdressesSecondairesRenseignee,
    adressesSecondairesDuMineur,
}) => {
    const hookForm = useForm({
        mode: 'onChange',
        criteriaMode: 'all',
    });

    const { handleSubmit, setValue } = hookForm;

    const [adresseIdentiqueMineur, setAdresseIdentiqueMineur] = useState(null);
    const [listeAdresses, setListeAdresses] = useState(dataAdressesSecondairesRenseignee || []);
    const [affichageFormulaire, setAffichageFormulaire] = useState(false);
    const [dataToSubmit, setDataToSubmit] = useState(null);
    const [ouvrirModalBan, setOuvrirModalBan] = useState(false);
    const [showQuestionAdresseIdentique, setShowQuestionAdresseIdentique] = useState(false);
    const [indicesAdressesMineurUtilisees, setIndicesAdressesMineurUtilisees] = useState([]);

    const hasAdressesMineur = adressesSecondairesDuMineur && adressesSecondairesDuMineur.length > 0;

    const getProchainIndexMineurDisponible = () => {
        if (!hasAdressesMineur) return -1;

        const premierIndexDisponible = adressesSecondairesDuMineur.findIndex(
            (_, index) => !indicesAdressesMineurUtilisees.includes(index),
        );

        return premierIndexDisponible !== -1 ? premierIndexDisponible : -1;
    };

    const prochainIndexMineurDisponible = getProchainIndexMineurDisponible();
    const hasAdressesMineurRestantes = hasAdressesMineur && prochainIndexMineurDisponible !== -1;

    useEffect(() => {
        if (
            !isLoadingAjoutAdresseSecondaireResponsableLegalMineur &&
            dataAjoutAdresseSecondaireResponsableLegalMineur &&
            dataAjoutAdresseSecondaireResponsableLegalMineur.uuidBrouillon
        ) {
            setOuvrirModalBan(false);
        }
    }, [
        isLoadingAjoutAdresseSecondaireResponsableLegalMineur,
        dataAjoutAdresseSecondaireResponsableLegalMineur,
    ]);

    useEffect(() => {
        if (dataAdressesSecondairesRenseignee && dataAdressesSecondairesRenseignee.length > 0) {
            setListeAdresses(dataAdressesSecondairesRenseignee);

            const indicesUtilises = [];
            dataAdressesSecondairesRenseignee.forEach((adresse) => {
                if (adresse.estCopieMineur === true) {
                    const indexDansMineur = adressesSecondairesDuMineur.findIndex(
                        (a) =>
                            a.numeroDeVoie === adresse.numeroDeVoie &&
                            a.extension === adresse.extension &&
                            a.typeEtNomDeVoie === adresse.typeEtNomDeVoie &&
                            a.lieuDit === adresse.lieuDit &&
                            a.codePostal === adresse.codePostal &&
                            a.ville === adresse.ville,
                    );
                    if (indexDansMineur !== -1 && !indicesUtilises.includes(indexDansMineur)) {
                        indicesUtilises.push(indexDansMineur);
                    }
                }
            });
            setIndicesAdressesMineurUtilisees(indicesUtilises);
        }
    }, [dataAdressesSecondairesRenseignee]);

    const suppressionLigneTableau = (table, index) => {
        table.splice(index, 1);
        return [...table];
    };

    const supprimerLigne = (idBouton) => {
        const adresseSupprimee = listeAdresses[idBouton];
        const nouvelleListe = suppressionLigneTableau(listeAdresses, idBouton);
        setListeAdresses(nouvelleListe);

        if (adresseSupprimee.estCopieMineur === true) {
            const indexDansMineur = adressesSecondairesDuMineur.findIndex(
                (a) =>
                    a.numeroDeVoie === adresseSupprimee.numeroDeVoie &&
                    a.extension === adresseSupprimee.extension &&
                    a.typeEtNomDeVoie === adresseSupprimee.typeEtNomDeVoie &&
                    a.lieuDit === adresseSupprimee.lieuDit &&
                    a.codePostal === adresseSupprimee.codePostal &&
                    a.ville === adresseSupprimee.ville,
            );
            if (indexDansMineur !== -1) {
                setIndicesAdressesMineurUtilisees(
                    indicesAdressesMineurUtilisees.filter((i) => i !== indexDansMineur),
                );
            }
        }
    };

    const getActions = (idBouton) => {
        if (adresseIdentiqueMineur === true) {
            return null;
        }
        return (
            <>
                <Button
                    icon="fr-fi-delete-fill"
                    variant="secondary"
                    onClick={() => supprimerLigne(idBouton)}
                    aria-label="Supprimer une adresse secondaire"
                    type="button"
                />
            </>
        );
    };

    const resetForm = () => {
        setValue('numeroDeVoie', '');
        setValue('extension', '');
        setValue('typeEtNomDeVoie', '');
        setValue('lieuDit', '');
        setValue('codePostal', '');
        setValue('ville', '');
    };

    const cacherFormulaire = () => {
        setAffichageFormulaire(false);
        setShowQuestionAdresseIdentique(false);
        setAdresseIdentiqueMineur(null);
        resetForm();
    };

    const gererQuestionAdresseIdentique = (valeur) => {
        setAdresseIdentiqueMineur(valeur);

        if (valeur === true && prochainIndexMineurDisponible !== -1) {
            const adresseCopiee = adressesSecondairesDuMineur[prochainIndexMineurDisponible];
            setValue('numeroDeVoie', adresseCopiee.numeroDeVoie || '');
            setValue('extension', adresseCopiee.extension || '');
            setValue('typeEtNomDeVoie', adresseCopiee.typeEtNomDeVoie || '');
            setValue('lieuDit', adresseCopiee.lieuDit || '');
            setValue('codePostal', adresseCopiee.codePostal || '');
            setValue('ville', adresseCopiee.ville || '');
        } else {
            resetForm();
        }
    };

    const gererAjoutAdresse = () => {
        if (hasAdressesMineurRestantes) {
            setShowQuestionAdresseIdentique(true);
        }

        setAffichageFormulaire(true);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
        reinitialiseApi(CONTEXT_CREER_COMPTE_ETAPE_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR);
        return () => {
            reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
            reinitialiseApi(
                CONTEXT_CREER_COMPTE_ETAPE_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR,
            );
        };
    }, []);

    const requetePostAdresse = (donnees) => {
        reinitialiseApi(CONTEXT_CREER_COMPTE_ETAPE_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR);
        postApi(
            usagerClientReducer,
            API_COMPTE_CREER_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR_URL,
            donnees,
            CONTEXT_CREER_COMPTE_ETAPE_ADRESSES_SECONDAIRES_RESPONSABLE_LEGAL_MINEUR,
        );
    };

    useEffect(() => {
        if (
            isLoadingAjoutAdresseSecondaireResponsableLegalMineur === false &&
            dataAjoutAdresseSecondaireResponsableLegalMineur
        ) {
            setEtapeAdresse(listeAdresses);
        }
    }, [
        isLoadingAjoutAdresseSecondaireResponsableLegalMineur,
        dataAjoutAdresseSecondaireResponsableLegalMineur,
    ]);

    const verifierBan = (data) => {
        const adresseAverifier = {
            numeroVoie: data.numeroDeVoie,
            libelleVoie: data.typeEtNomDeVoie,
            codePostal: data.codePostal,
            ville: data.ville,
            extension: data.extension,
            lieuDit: data.lieuDit,
        };

        reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
        postApi(
            usagerClientReducer,
            API_POST_VERIFIER_BAN,
            adresseAverifier,
            CONTEXT_POST_VERIFIER_BAN,
        );
        setDataToSubmit(data);
        setOuvrirModalBan(true);
    };

    const ajouterAdresseALaListe = (adresse, indexMineurUtilise = -1) => {
        setListeAdresses([...listeAdresses, adresse]);
        if (indexMineurUtilise !== -1) {
            setIndicesAdressesMineurUtilisees([
                ...indicesAdressesMineurUtilisees,
                indexMineurUtilise,
            ]);
        }
    };

    const onRetourEtape = () => {
        setAffichageFormulaire(false);
        setShowQuestionAdresseIdentique(false);
        resetForm();
        retourEtape(listeAdresses);
    };

    const onEtapeSuivante = async () => {
        const adressesSansFlag = listeAdresses.map(({ estCopieMineur, ...adresse }) => adresse);
        const data = FormaterCreationListeAdressesSecondaires(adressesSansFlag, uuidBrouillon);
        requetePostAdresse(data);
        setAffichageFormulaire(false);
        setShowQuestionAdresseIdentique(false);
        resetForm();
    };

    const fermerModal = () => {
        setOuvrirModalBan(false);
        reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
    };

    const conserver = () => {
        fermerModal();
        const indexMineurUtilise =
            adresseIdentiqueMineur === true ? prochainIndexMineurDisponible : -1;
        ajouterAdresseALaListe(
            {
                ...dataToSubmit,
                hasVerificationBan: false,
                estCopieMineur: adresseIdentiqueMineur === true,
            },
            indexMineurUtilise,
        );
        setAffichageFormulaire(false);
        setShowQuestionAdresseIdentique(false);
        setAdresseIdentiqueMineur(null);
        resetForm();
    };

    const remplacer = () => {
        fermerModal();
        const indexMineurUtilise =
            adresseIdentiqueMineur === true ? prochainIndexMineurDisponible : -1;
        ajouterAdresseALaListe(
            {
                ...dataToSubmit,
                numeroDeVoie: dataBan.numeroVoie,
                typeEtNomDeVoie: dataBan.libelleVoie,
                extension: dataBan.extension,
                lieuDit: dataBan.lieuDit,
                codePostal: dataBan.codePostal,
                ville: dataBan.ville,
                hasVerificationBan: true,
                estCopieMineur: adresseIdentiqueMineur === true,
            },
            indexMineurUtilise,
        );
        setAffichageFormulaire(false);
        setShowQuestionAdresseIdentique(false);
        setAdresseIdentiqueMineur(null);
        resetForm();
    };

    return (
        <div>
            <SegmentLoader isLoading={isLoadingAjoutAdresseSecondaireResponsableLegalMineur}>
                {ouvrirModalBan && (
                    <ModalBan
                        residence="secondaire"
                        fermerModal={fermerModal}
                        data={dataBan}
                        isLoading={isLoadingBan}
                        remplacer={remplacer}
                        conserver={conserver}
                    />
                )}

                <TableAdressesComponent listeAdresses={listeAdresses} actions={getActions} />

                {showQuestionAdresseIdentique && hasAdressesMineurRestantes && (
                    <Row>
                        <Col col={12}>
                            <FieldsetControl
                                id="adresseIdentiqueAjout"
                                legend="L'adresse de résidence secondaire du responsable légal est-elle la même que l'adresse de résidence secondaire du détenteur mineur ?"
                            >
                                <RadioControl
                                    id="adresseIdentiqueOui"
                                    label="Oui"
                                    name="adresseIdentique"
                                    checked={adresseIdentiqueMineur === true}
                                    onChange={() => gererQuestionAdresseIdentique(true)}
                                    disabled={adresseIdentiqueMineur !== null}
                                />
                                <RadioControl
                                    id="adresseIdentiqueNon"
                                    label="Non"
                                    name="adresseIdentique"
                                    checked={adresseIdentiqueMineur === false}
                                    onChange={() => gererQuestionAdresseIdentique(false)}
                                    disabled={adresseIdentiqueMineur !== null}
                                />
                            </FieldsetControl>
                        </Col>
                    </Row>
                )}

                {affichageFormulaire && (
                    <FormProvider {...hookForm}>
                        <form onSubmit={handleSubmit(verifierBan)}>
                            <Row>
                                <Col col={12}>
                                    <InfoChampsObligatoire />
                                </Col>
                            </Row>
                            <FormulaireAdresseSimple />
                            <Row className="margin-top-lg">
                                <Col>
                                    <ButtonGroup inline align="right">
                                        <Button
                                            name="Annuler"
                                            variant="secondary"
                                            onClick={cacherFormulaire}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            name="validerAdresse"
                                            type="submit"
                                            variant="primary"
                                        >
                                            Valider
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </form>
                    </FormProvider>
                )}

                {!affichageFormulaire && !showQuestionAdresseIdentique && (
                    <div>
                        <Button
                            name="ajouterAdresse"
                            variant="secondary"
                            onClick={gererAjoutAdresse}
                            icon="fr-icon-add-line"
                            iconPosition="left"
                        >
                            Ajouter une adresse de résidence secondaire
                        </Button>
                    </div>
                )}

                <Row className="margin-top-lg">
                    <Col>
                        <ButtonGroup inline align="right">
                            <Button
                                isLoading={isLoadingAjoutAdresseSecondaireResponsableLegalMineur}
                                name="etapePrecedente"
                                variant="secondary"
                                onClick={onRetourEtape}
                            >
                                Retour
                            </Button>
                            <Button
                                isLoading={isLoadingAjoutAdresseSecondaireResponsableLegalMineur}
                                name="etapeSuivante"
                                variant="primary"
                                onClick={onEtapeSuivante}
                                disabled={affichageFormulaire}
                            >
                                Accéder à l'étape suivante
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </SegmentLoader>
        </div>
    );
};

CreerCompteAdresseSecondaireResponsableLegal.propTypes = {
    uuidBrouillon: PropTypes.string.isRequired,
    setEtapeAdresse: PropTypes.func.isRequired,
    retourEtape: PropTypes.func.isRequired,
    postApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    isLoadingBan: PropTypes.bool,
    dataBan: PropTypes.any,
    isLoadingAjoutAdresseSecondaireResponsableLegalMineur: PropTypes.bool,
    dataAjoutAdresseSecondaireResponsableLegalMineur: PropTypes.shape({
        uuidBrouillon: PropTypes.string.isRequired,
    }),
    dataAdressesSecondairesRenseignee: PropTypes.arrayOf(
        PropTypes.shape({
            hasVerificationBan: PropTypes.bool,
            numeroDeVoie: PropTypes.string,
            typeEtNomDeVoie: PropTypes.string.isRequired,
            extension: PropTypes.string,
            lieuDit: PropTypes.string,
            codePostal: PropTypes.string.isRequired,
            ville: PropTypes.string.isRequired,
        }),
    ),
    adressesSecondairesDuMineur: PropTypes.arrayOf(
        PropTypes.shape({
            hasVerificationBan: PropTypes.bool,
            numeroDeVoie: PropTypes.string,
            typeEtNomDeVoie: PropTypes.string.isRequired,
            extension: PropTypes.string,
            lieuDit: PropTypes.string,
            codePostal: PropTypes.string.isRequired,
            ville: PropTypes.string.isRequired,
        }),
    ),
};

export default connect(
    (state) => {
        const {
            isLoading: isLoadingAjoutAdresseSecondaireResponsableLegalMineur,
            data: dataAjoutAdresseSecondaireResponsableLegalMineur,
        } = state.compte.creer.ajoutAdresseSecondaireResponsableLegalMineur;
        const { isLoading: isLoadingBan, data: dataBan } = state.adresseVerificationBAN.verifier;
        return {
            isLoadingBan,
            dataBan,
            isLoadingAjoutAdresseSecondaireResponsableLegalMineur,
            dataAjoutAdresseSecondaireResponsableLegalMineur,
        };
    },
    {
        postApi: postAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(CreerCompteAdresseSecondaireResponsableLegal);
